import React from 'react'
import classNames from 'classnames'
import TableRow from './table-row'
import { getValue, getFirstValue } from '../../utils/product-schema'
import ImgixImage from '../helpers/imgix-image'
import Cell from './cell'
import MoreInformation from './more-information'
import { Button } from '../../atoms'

const addExtraParams = (href, position, tableName: string | undefined) => {
  try {
    const url = new URL(href)
    url.searchParams.set('position', `deal-${position}`)
    if (tableName) {
      url.searchParams.set('table', tableName)
    }
    return url.toString()
  } catch (e) {
    return href
  }
}

const moreInformationFilter = ({ type, content }, product) => {
  if (type === 'html') {
    return content.filter((c) => getValue(c, product)).length > 0
  } else if (type === 'list') {
    return content.filter(({ value }) => getValue(value, product)).length > 0
  }
}

export const ProductTableV2 = ({
  badge,
  cells,
  clickableRow,
  contentfulId,
  data,
  dataset,
  disablingField,
  image,
  info,
  footer,
  moreInformation,
  name,
  primaryCta,
  secondaryCta,
  schema,
  title,
  brand,
  variant,
  expandFirstRow,
}) => {
  return (
    <div
      className='ProductTable__rows'
      data-dataset-id={dataset.sys?.id}
      data-schema-id={schema.sys?.id}
      data-contentful-table-id={contentfulId}
      data-contentful-name={name}
      data-product-table-v2
    >
      {data.map((d, index) => {
        const primaryCtaValue = Array.isArray(primaryCta)
          ? primaryCta.reduce(
              (result, { href, text, validIf }) => {
                const hrefValue = getValue(href, d)
                const textValue = getValue(text, d)
                const isValid = !validIf || getValue({ valueFrom: validIf }, d)
                return !result.href && hrefValue && isValid
                  ? { href: hrefValue, text: textValue }
                  : result
              },
              { href: null, text: null },
            )
          : { href: getValue(primaryCta.href, d), text: getValue(primaryCta.text, d) }

        const clickableRowValue = addExtraParams(
          Array.isArray(clickableRow)
            ? clickableRow.reduce((result, href) => {
                const hrefValue = getValue(href, d)
                const isValid = !href.validIf || getValue({ valueFrom: href.validIf }, d)
                return !result && hrefValue && isValid ? hrefValue : result
              }, null)
            : getValue(clickableRow, d),
          index + 1,
          dataset.fields?.name,
        )

        const eligibilityScore =
          cells.find((c) => c.content.valueFrom === 'eligibility.likelihood') &&
          getValue({ valueFrom: 'eligibility.likelihood' }, d)

        return (
          <TableRow
            key={d._id}
            badge={getValue(badge, d)}
            disabled={disablingField && !getValue(disablingField, d)}
            productId={d._id}
            className={classNames({
              'TableRow--withLargeBadge':
                getValue(badge, d)?.length && getValue(badge, d).length > 60,
            })}
            preapproved={eligibilityScore === 100}
            href={clickableRowValue}
          >
            <div className='TableRow__inner'>
              <div className='Rowheader'>
                {brand !== 'money' && <ImgixImage src={getValue(image, d)} height={'67px'} />}
                <h3 className='Rowheader__title'>{getValue(title, d)}</h3>
              </div>
              <div className='ProductTable__cells'>
                {brand === 'money' && <ImgixImage src={getValue(image, d)} />}
                {cells.map((cell, index) => {
                  return (
                    <Cell
                      className={`ProductTable__Cell--${index + 1}`}
                      key={index}
                      label={getValue(cell.label, d)}
                      content={
                        getFirstValue(cell.content, d) === true
                          ? 'Yes'
                          : getFirstValue(cell.content, d) === false
                          ? 'No'
                          : getFirstValue(cell.content, d)
                      }
                      format={cell.format}
                      brand={brand}
                    />
                  )
                })}
                {clickableRowValue && (!disablingField || getValue(disablingField, d)) ? (
                  <div className='ProductTable__cta'>
                    <Button
                      brand={brand}
                      variant='primary'
                      size='md'
                      dataClickTracking={{
                        component_category: 'component',
                        component_id: dataset.sys?.id,
                        component_name: 'table',
                        contentful_name: dataset.fields?.name,
                        contentful_variant: variant,
                        link_path: primaryCtaValue.href,
                        additionalProps: {
                          brand: dataset.fields?.brand,
                          product_id: d._id,
                          index: index,
                          text: primaryCtaValue.text,
                        },
                      }}
                    >
                      {primaryCtaValue.text}
                    </Button>
                    {secondaryCta && getValue(secondaryCta.validIf, d) && (
                      <button className='ProductTable__cta--secondary'>
                        {getValue(secondaryCta.text, d)}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className='ProductTable__cta--withSpacing' />
                )}
              </div>
              {info && (
                <div
                  className={classNames('ProductTable__info', {
                    'ProductTable__info--noBold': !info.label,
                  })}
                >
                  <div>{info.label}</div>
                  {info.content.map((c, index) => (
                    <div key={index}>{getValue(c, d)}</div>
                  ))}
                </div>
              )}
              {footer && (
                <div className='ProductTable__footer'>
                  {footer.label}
                  {getValue(footer.text, d)}
                </div>
              )}
            </div>
            {moreInformation &&
              moreInformation.filter((m) => moreInformationFilter(m, d)).length > 0 && (
                <details
                  data-product-table-v2-more-information
                  className='ProductTable__moreInfo'
                  open={expandFirstRow && index === 0}
                >
                  <summary>More Information</summary>
                  <div className='ProductTable__moreInfo__blocks'>
                    {moreInformation.map((moreInfo, moreInfoKey) => {
                      return <MoreInformation key={moreInfoKey} product={d} {...moreInfo} />
                    })}
                  </div>
                </details>
              )}
          </TableRow>
        )
      })}
    </div>
  )
}
